import { useQuery, UseQueryResult } from "@tanstack/react-query";
import config from "../config.json";
import { IPageable, Params } from "../types/table/interfaces.type";
import { pageAsOptions } from "../utils/commonDataUtils";
import { fetchOperator } from "./OperatorsService";
import { createAxiosInstance } from "./ServicesHelpers";

export interface ICountry {
  id: number;
  name: string;
  mcc: string[];
}

const axiosInstance = createAxiosInstance(config.ADMIN_API_BASE_URL);

export const fetchCountries = async (
  params: Params<ICountry>
): Promise<IPageable<ICountry>> => {
  try {
    console.log("Fetching countries...");
    const response = await axiosInstance.get("/countries", { params });
    console.log("Raw response from /countries:", response.data);
    return response.data;
  } catch (error: any) {
    console.error("Error fetching countries:", error.message || error);
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while fetching the countries."
    );
  }
};

export const fetchCountry = async (id: ICountry["id"]): Promise<ICountry> => {
  try {
    console.log(`Fetching countries/${id} ...`);
    const response = await axiosInstance.get(`/countries/${id}`);
    console.log(`Raw response from /countries/${id}:`, response.data);
    return response.data;
  } catch (error: any) {
    console.error(`Error fetching countries/${id}:`, error.message || error);
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while fetching the countries/${id}.`
    );
  }
};

export const useCountryQuery = (
  params: Params<ICountry>
): UseQueryResult<IPageable<ICountry>, Error> => {
  const queryKey = ["country", JSON.stringify(params)];
  return useQuery({
    queryKey,
    queryFn: () => fetchCountries(params),
  });
};

export const useCountriesQuery = (
  params: Params<ICountry> = {}
): UseQueryResult<ReturnType<typeof pageAsOptions>, Error> => {
  const queryKey = ["countries", JSON.stringify(params)];
  return useQuery({
    queryKey,
    queryFn: async () => {
      const mergedParams = {
        sort: "name",
        size: config.MAX_PAGE_SIZE,
        ...params,
      };
      const countriesPage = await fetchCountries(mergedParams);
      return pageAsOptions(countriesPage);
    },
  });
};

export const getOperatorCountryNameOptions = (operatorId: number) => ({
  queryKey: ["countryOperator", operatorId],
  queryFn: async (): Promise<ICountry | null> => {
    try {
      const operator = await fetchOperator(operatorId);
      if (!operator?.countryId) return null;
      const country = await fetchCountry(operator.countryId);
      return country || null;
    } catch {
      return null;
    }
  },
  select: (country: ICountry | null): string => country?.name || "-",
});

export const getCountryNameOptions = (countryId: number) => ({
  queryKey: ["country", countryId],
  queryFn: async (): Promise<string> => {
    const country = await fetchCountry(countryId);
    return country?.name;
  },
  select: (name: string) => name,
});
